import React, { memo, useCallback, useMemo } from 'react';
import { FormControl } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useController, useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';

// Memo keeps the date picker from re-rendering when sibling fields change.
function KeyboardDatePickerInput(props) {

  const { variant, label, name, onChange, ...keyboardDatePickerProps } = props;
  const formContext = useFormContext();
  const { field, fieldState: { error } } = useController({
    name,
    control: formContext?.control,
    defaultValue: null
  });

  // Stable date change handler avoids passing a new callback into MUI on every parent render.
  const handleChange = useCallback((newValue) => {
    // newValue is a dayjs object provided by MUI
    field.onChange(newValue);

    if (onChange) {
      onChange(newValue);
    }
  }, [field, onChange]);

  // Slot props are nested objects; memoizing prevents unnecessary text-field remount work.
  const slotProps = useMemo(() => ({
    textField: {
      variant: variant || "outlined",
      error: !!error,
      helperText: error?.message,
      label: label,
      onBlur: field.onBlur,
    },
    openPickerButton: { className: "me-0" }
  }), [error, field.onBlur, label, variant]);

  // Stable close handler keeps blur validation behavior without recreating the callback.
  const handleClose = useCallback(() => field.onBlur(), [field]);

  return (
    <FormControl fullWidth>
      <DesktopDatePicker
        // In MUI 9, format is the prop for the display mask
        format="MM/DD/YYYY"
        value={field.value ? dayjs(field.value) : null}
        onChange={handleChange}
        onClose={handleClose}  // Triggers validation when the picker closes
        {...keyboardDatePickerProps}
        slotProps={slotProps}
      />
    </FormControl>
  );
}

// Memoization is effective because AboutMeMinimalForm now passes stable date props and handlers.
export default memo(KeyboardDatePickerInput);
